/**
 * The browser half of Better Auth.
 *
 * This is the official `better-auth/react` client rather than a hand-rolled
 * `fetch` wrapper. It bundles cleanly under Bun's bundler (34 modules, ~52 KB
 * minified, no Node builtins reached), it derives every endpoint path from the
 * same base path the server mounts, and it is the piece that stays correct when
 * the server later grows a plugin — a wrapper would have to be re-derived by
 * hand each time.
 *
 * Only the network layer is taken from it. The session *state* lives in
 * `session.ts` on this app's own store, because the gate needs a three-way
 * answer ("loading" is not "signed out") resolved exactly once at boot, and
 * because the 401 that arrives from the sync transport has to reach the same
 * state as a sign-out does.
 */

import { createAuthClient } from "better-auth/react";

/** Must match `AUTH_BASE_PATH` in `src/server/auth.ts`. */
export const AUTH_BASE_PATH = "/api/auth";

/**
 * Better Auth defaults the base URL to `window.location.origin`, which is what
 * we want in the browser and unusable everywhere else: `fetch` rejects a
 * relative URL outright, so merely importing this module would throw in Bun's
 * test runtime. The placeholder is never reached by a real request.
 */
function origin(): string {
  const here = typeof window === "undefined" ? undefined : window.location?.origin;
  return here ?? "http://punchboard.invalid";
}

export const authClient = createAuthClient({
  baseURL: origin(),
  basePath: AUTH_BASE_PATH,
  fetchOptions: {
    // Set once, here, so it cannot be forgotten on an individual call: the
    // session cookie is the only thing that authorises a write, and a request
    // without it is a silent 401 rather than an error anyone sees.
    credentials: "same-origin",
    // Read `fetch` at call time rather than letting the transport capture it
    // when this module happens to load. The default binding makes import order
    // decide the transport, which is wrong the moment anything wraps `fetch`
    // after boot — and untestable, since a stub installed later is ignored.
    customFetchImpl: (input, init) => fetch(input as RequestInfo, init as RequestInit),
  },
});
